import { useEffect } from 'react';
import ContentGrid from '../../../components/ContentGrid';
import AppBar from '../../../components/AppBar';
import styles from './page.module.css';
import GlobalTabs from '../../../components/GlobalTabs';
import { GlobalMicroButton } from '../../../components/GlobalMicroButton';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, functions } from '../../../lib/firebase';
import AppFooter from '../../../components/AppFooter';
import { useDocuments } from '../../../hooks/useDocuments';
import { useTheme } from '../../../context/ThemeProvider';
import { useUser } from '../../../context/UserContext';
import DoubleConfirmationModal from '../../../components/DoubleConfirmationModal';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../../components/Toast';
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useLocale } from '../../../context/DynamicLocaleProvider';
import { loadDocumentImages, isDocxFileNameOrType } from './controller';
import { db, storage } from '../../../lib/firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { Favicon } from '../../../utils/favicon';
import { Document } from '../../../components/DocumentTile';
import { GlobalInput } from '../../../components/GlobalInput';
import { GlobalTextarea } from '../../../components/GlobalTextarea';
import { GlobalButton } from '../../../components/GlobalButton';
import SmallServiceCard from '../../../components/SmallServiceCard';
import GlobalSidebar from '../../../components/GlobalSidebar';
import GlobalGptAccessPanel from '../../../components/GlobalGptAccessPanel';
import GlobalCollapsibleButton from '../../../components/GlobalCollapsibleButton';
import { buildThesconAiPath } from '../../../utils/thesconAiNavigation';
import { fileTypeGraphicPathOrGeneric } from '../../../utils/fileTypeGraphics';
import { getEmbeddingBadgeKind } from '../../../utils/embeddingBadge';

// Helper function to format date nicely
const formatDate = (date: Date | Timestamp | string | undefined, locale: string): string => {
  if (!date) return '';
  try {
    const dateObj = date instanceof Timestamp 
      ? date.toDate() 
      : typeof date === 'string' 
        ? new Date(date) 
        : date;
    
    if (isNaN(dateObj.getTime())) return '';
    
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(dateObj);
  } catch {
    return '';
  }
};

export default function DocumentPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const knowledgeId = searchParams.get('knowledgeId');
  const { user, role } = useUser();
  const { documents } = useDocuments(user);
  const [directDocument, setDirectDocument] = useState<Document | null>(null);
  const document = documents.find(doc => doc.id === documentId) || directDocument;

  const { message, type, isVisible, showToast, hideToast } = useToast();
  const { t, locale } = useLocale();
  const [pageImages, setPageImages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Debug: Log pageImages changes
  useEffect(() => {

  }, [pageImages]);
  const [showModal, setShowModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Metadata editing states
  const [isSavingMetadata, setIsSavingMetadata] = useState(false);
  const [editedMetadata, setEditedMetadata] = useState<any>({});
  const [projectDocumentData, setProjectDocumentData] = useState<any>(null);
  /** Live count of chunk docs in Firestore (undefined loading, null if unreadable) */
  const [verifiedChunkCount, setVerifiedChunkCount] = useState<number | null | undefined>(undefined);

  // Ask document sidebar state
  const [showAskDocumentSidebar, setShowAskDocumentSidebar] = useState(false);

  // Fetch document directly from Firestore if not found in useDocuments
  useEffect(() => {
    if (!documentId || !user?.uid) {
      setDirectDocument(null);
      return;
    }

    // Check if document is already in the documents array
    const foundInDocuments = documents.find(doc => doc.id === documentId);
    if (foundInDocuments) {
      setDirectDocument(null);
      return;
    }

    // If not found and documents have finished loading (or after a short delay), try direct fetch
    let isCancelled = false;
    const fetchDirect = async () => {
      try {
        // If knowledgeId is provided, try to fetch from knowledge documents first
        if (knowledgeId) {
          try {
            const knowledgeDocRef = doc(db, 'knowledge', knowledgeId, 'documents', documentId);
            const knowledgeDocSnap = await getDoc(knowledgeDocRef);

            if (!isCancelled && knowledgeDocSnap.exists()) {
              const data = knowledgeDocSnap.data();
              setProjectDocumentData({
                ...data,
                id: knowledgeDocSnap.id,
                storagePath: data.storagePath,
                fileUrl: data.fileUrl,
              });

              const fetchedDoc: Document = {
                id: knowledgeDocSnap.id,
                documentId: data.documentId || knowledgeDocSnap.id,
                title: data.filename || 'Untitled',
                sender: 'Knowledge Document',
                createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() :
                           data.creationDate instanceof Timestamp ? data.creationDate.toDate() :
                           new Date(),
                detectedDocumentCategory: data.type || 'Knowledge Document',
                status: 'submitted',
                taskCount: 0,
                doneTaskCount: 0,
                userId: data.userId || user.uid,
                fileName: data.filename || 'document',
              };

              setDirectDocument(fetchedDoc);
              return;
            }
          } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
              console.error('❌ Error fetching knowledge document:', error);
            }
          }
        }

        // If projectId is provided, try to fetch from project documents
        if (projectId) {
          try {
            const projectDocRef = doc(db, 'projects', projectId, 'documents', documentId);
            const projectDocSnap = await getDoc(projectDocRef);
            
            if (!isCancelled && projectDocSnap.exists()) {
              const data = projectDocSnap.data();
              // Store raw project document data
              setProjectDocumentData({
                ...data,
                id: projectDocSnap.id,
              });
              
              const fetchedDoc: Document = {
                id: projectDocSnap.id,
                documentId: data.documentId || projectDocSnap.id,
                title: data.filename || 'Untitled',
                sender: 'Project Document',
                createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : 
                           data.creationDate instanceof Timestamp ? data.creationDate.toDate() : 
                           new Date(),
                detectedDocumentCategory: data.type || 'Project Document',
                status: 'submitted', // Project documents don't have invoice status
                taskCount: 0,
                doneTaskCount: 0,
                userId: data.userId || user.uid,
                fileName: data.filename || 'document',
              };

              setDirectDocument(fetchedDoc);
              return; // Found project document, exit early
            }
          } catch (error) {
            // Silently continue to try invoices collection
            if (process.env.NODE_ENV !== 'production') {
              console.error('❌ Error fetching project document:', error);
            }
          }
        }

        // Try invoices collection (existing behavior)
        const docRef = doc(db, 'invoices', documentId);
        const docSnap = await getDoc(docRef);
        
        if (!isCancelled && docSnap.exists()) {
          const data = docSnap.data();
          const fetchedDoc: Document = {
            id: docSnap.id,
            documentId: data.documentId || '',
            title: data.name || data.title || 'Untitled',
            sender: data.sender || 'Unknown',
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : 
                       data.creationDate instanceof Timestamp ? data.creationDate.toDate() : 
                       new Date(),
            detectedDocumentCategory: data.detectedDocumentCategory || 'Unknown',
            status: data.status || 'submitted',
            taskCount: data.taskCount || 0,
            doneTaskCount: data.doneTaskCount || 0,
            currency: data.currency,
            dueDate: data.dueDate,
            invoiceDate: data.invoiceDate,
            invoiceNumber: data.invoiceNumber,
            paymentTerms: data.paymentTerms,
            receiver: data.receiver,
            senderWebsiteUrl: data.senderWebsiteUrl,
            summary: data.summary,
            subtotal: data.subtotal,
            taxAmount: data.taxAmount,
            totalAmount: data.totalAmount,
            userId: data.userId,
            content: data.content,
            fileName: data.filename || data.fileName, // Firestore uses 'filename' (lowercase)
          };

          setDirectDocument(fetchedDoc);
        } else if (!isCancelled) {

          setDirectDocument(null);
        }
      } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Error fetching document directly:', error);
      }
        if (!isCancelled) {
          setDirectDocument(null);
        }
      }
    };

    // Small delay to let useDocuments finish loading first
    const timeoutId = setTimeout(fetchDirect, 500);
    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, user?.uid, documents.length, projectId, knowledgeId]);

  useEffect(() => {
    setVerifiedChunkCount(undefined);
    if (!projectDocumentData?.documentId || (!knowledgeId && !projectId)) {
      return;
    }
    // Knowledge chunk docs are restricted to internal_admin in rules.
    if (knowledgeId && role !== 'internal_admin') {
      setVerifiedChunkCount(null);
      return;
    }
    let cancelled = false;
    const logicalId = String(projectDocumentData.documentId);
    (async () => {
      try {
        const q = projectId
          ? query(
              collection(db, 'projects', projectId, 'documentChunks'),
              where('documentId', '==', logicalId)
            )
          : query(
              collection(db, 'knowledge', knowledgeId!, 'documentKnowledge'),
              where('documentId', '==', logicalId)
            );
        const snap = await getCountFromServer(q);
        if (!cancelled) setVerifiedChunkCount(snap.data().count);
      } catch {
        if (!cancelled) setVerifiedChunkCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [knowledgeId, projectId, projectDocumentData?.documentId, role]);

  useEffect(() => {

    if (!documentId) {

      setPageImages([]);
      setCurrentImageIndex(0);
      setIsLoading(false);
      return;
    }
    
    // Even if document is not loaded yet, try to load images using documentId from URL
    const docId = document?.documentId || document?.id || documentId;
    const fileNm = document?.fileName;
    const usrId = document?.userId || user?.uid;
    
    if (!docId) {

      setPageImages([]);
      setCurrentImageIndex(0);
      setIsLoading(false);
      return;
    }
    
    let isCancelled = false;
    setIsLoading(true);
    (async () => {
      try {
        const storagePath = projectDocumentData?.storagePath;
        const fileUrl = projectDocumentData?.fileUrl;
        const contentType = projectDocumentData?.contentType;
        
        const urls = await loadDocumentImages(
          docId, 
          fileNm, 
          document?.id || docId, 
          usrId, 
          document?.createdAt, 
          projectId || undefined,
          storagePath,
          fileUrl,
          knowledgeId || undefined,
          contentType
        );
        if (!isCancelled) {
          setPageImages(urls);
          setCurrentImageIndex(0);
          setIsLoading(false);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('❌ Error loading document images:', error);
        }
        if (!isCancelled) {
          setPageImages([]);
          setIsLoading(false);
        }
      }
    })();
    return () => { 
      isCancelled = true;
      setIsLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, document?.id, document?.documentId, document?.fileName, document?.userId, user?.uid, projectId, knowledgeId, projectDocumentData?.storagePath, projectDocumentData?.fileUrl, projectDocumentData?.contentType]);

  // Initialize edited metadata when document loads
  useEffect(() => {
    if (projectDocumentData) {
      // For project documents, use project-specific fields
      setEditedMetadata({
        filename: projectDocumentData.filename || '',
        version: projectDocumentData.version || '',
      });
    } else if (document) {
      // For invoice documents, use invoice-specific fields
      setEditedMetadata({
        title: document.title || '',
        sender: document.sender || '',
        receiver: document.receiver || '',
        invoiceNumber: document.invoiceNumber || '',
        invoiceDate: document.invoiceDate || '',
        dueDate: document.dueDate || '',
        paymentTerms: document.paymentTerms || '',
        currency: document.currency || '',
        subtotal: document.subtotal || '',
        taxAmount: document.taxAmount || '',
        totalAmount: document.totalAmount || '',
        senderWebsiteUrl: document.senderWebsiteUrl || '',
        summary: document.summary || '',
        detectedDocumentCategory: document.detectedDocumentCategory || '',
      });
    }
  }, [document, projectDocumentData]);

  const navigate = useNavigate();

  const handleDelete = async () => {
    if (!document?.documentId) return;
    setIsDeleting(true);
    try {
      const deleteFn = httpsCallable(functions, 'deleteDocumentWithDependencies');
      const result = await deleteFn({
        documentId: document.documentId,
        knowledgeId: knowledgeId || undefined,
        projectId: projectId || undefined,
      });
      const data = result.data as { success?: boolean };
      if (!data?.success) throw new Error('Delete failed');
      navigate(knowledgeId ? `/knowledge/${knowledgeId}` : projectId ? `/project/${projectId}` : '/home');
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Document delete failed:', error);
      }
    } finally {
      setIsDeleting(false);
      setShowModal(false);
    }
  };

  const handleCancel = () => setShowModal(false);

  // Save metadata changes
  const handleSaveMetadata = async () => {
    if (!document?.id || !documentId) {
      showToast(t('document_page_missing_info'), 'error');
      return;
    }

    setIsSavingMetadata(true);
    try {
      // Determine which collection to update
      let docRef;
      let sourceData: any;
      
      if (knowledgeId && projectDocumentData) {
        docRef = doc(db, 'knowledge', knowledgeId, 'documents', documentId);
        sourceData = projectDocumentData;
      } else if (projectId && projectDocumentData) {
        docRef = doc(db, 'projects', projectId, 'documents', documentId);
        sourceData = projectDocumentData;
      } else {
        docRef = doc(db, 'invoices', documentId);
        sourceData = document;
      }

      const updateData: any = {};
      
      // Only update fields that have changed
      Object.keys(editedMetadata).forEach((key) => {
        const value = editedMetadata[key];
        const currentValue = sourceData[key];
        
        if (value !== undefined && JSON.stringify(value) !== JSON.stringify(currentValue)) {
          // Map frontend field names to Firestore field names for invoices
          if (!projectId && !knowledgeId && key === 'title') {
            updateData.name = value;
            updateData.title = value;
          } else if ((projectId || knowledgeId) && key === 'version') {
            // Convert version string to number for project documents
            const versionNum = parseFloat(value);
            if (!isNaN(versionNum)) {
              updateData[key] = versionNum;
            } else if (value === '') {
              // Allow clearing version
              updateData[key] = null;
            }
          } else {
            updateData[key] = value;
          }
        }
      });

      if (Object.keys(updateData).length > 0) {
        updateData.updatedAt = Timestamp.now();
        await updateDoc(docRef, updateData);
        showToast(t('document_page_metadata_saved') || 'Metadata saved successfully', 'success');
        // Refresh document data
        await refreshDocument();
      } else {
        showToast(t('document_page_no_changes') || 'No changes to save', 'info');
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Error saving metadata:', error);
      }
      showToast(t('document_page_metadata_save_error') || 'Failed to save metadata', 'error');
    } finally {
      setIsSavingMetadata(false);
    }
  };

  // Refresh document data after status change
  const refreshDocument = async () => {
    if (!documentId || !user?.uid) return;
    
    try {
      // Try knowledge document first if knowledgeId is provided
      if (knowledgeId) {
        try {
          const knowledgeDocRef = doc(db, 'knowledge', knowledgeId, 'documents', documentId);
          const knowledgeDocSnap = await getDoc(knowledgeDocRef);

          if (knowledgeDocSnap.exists()) {
            const data = knowledgeDocSnap.data();
            setProjectDocumentData({ ...data, id: knowledgeDocSnap.id });
            const updatedDoc: Document = {
              id: knowledgeDocSnap.id,
              documentId: data.documentId || knowledgeDocSnap.id,
              title: data.filename || data.name || 'Untitled',
              sender: 'Knowledge Document',
              createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(),
              detectedDocumentCategory: data.type || 'Knowledge Document',
              status: 'submitted',
              taskCount: 0,
              doneTaskCount: 0,
              userId: data.userId || user.uid,
              fileName: data.filename || 'document',
            };
            setDirectDocument(updatedDoc);
            return;
          }
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') {
            console.error('❌ Error refreshing knowledge document:', error);
          }
        }
      }

      // Try project document if projectId is provided
      if (projectId) {
        try {
          const projectDocRef = doc(db, 'projects', projectId, 'documents', documentId);
          const projectDocSnap = await getDoc(projectDocRef);
          
          if (projectDocSnap.exists()) {
            const data = projectDocSnap.data();
            // Store raw project document data
            setProjectDocumentData({
              ...data,
              id: projectDocSnap.id,
            });
            
            const updatedDoc: Document = {
              id: projectDocSnap.id,
              documentId: data.documentId || projectDocSnap.id,
              title: data.filename || data.name || 'Untitled',
              sender: 'Project Document',
              createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : 
                         data.creationDate instanceof Timestamp ? data.creationDate.toDate() : 
                         new Date(),
              detectedDocumentCategory: data.type || 'Project Document',
              status: 'submitted',
              taskCount: 0,
              doneTaskCount: 0,
              userId: data.userId || user.uid,
              fileName: data.filename || 'document',
            };
            setDirectDocument(updatedDoc);
            return;
          }
        } catch (error) {
          // Silently continue to try invoices collection
          if (process.env.NODE_ENV !== 'production') {
            console.error('❌ Error fetching project document:', error);
          }
        }
      }

      // Try invoices collection
      const docRef = doc(db, 'invoices', documentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const updatedDoc: Document = {
          id: docSnap.id,
          documentId: data.documentId || '',
          title: data.name || data.title || 'Untitled',
          sender: data.sender || 'Unknown',
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : 
                     data.creationDate instanceof Timestamp ? data.creationDate.toDate() : 
                     new Date(),
          detectedDocumentCategory: data.detectedDocumentCategory || 'Unknown',
          status: data.status || 'submitted',
          taskCount: data.taskCount || 0,
          doneTaskCount: data.doneTaskCount || 0,
          currency: data.currency,
          dueDate: data.dueDate,
          invoiceDate: data.invoiceDate,
          invoiceNumber: data.invoiceNumber,
          paymentTerms: data.paymentTerms,
          receiver: data.receiver,
          senderWebsiteUrl: data.senderWebsiteUrl,
          summary: data.summary,
          subtotal: data.subtotal,
          taxAmount: data.taxAmount,
          totalAmount: data.totalAmount,
          userId: data.userId,
          content: data.content,
          fileName: data.filename || data.fileName,
        };
        setDirectDocument(updatedDoc);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Error refreshing document:', error);
      }
    }
  };


  const nextImage = () => {
    setIsLoading(true);
    setIsLandscape(false); // Reset landscape state when changing images
    setCurrentImageIndex((prev) => (prev + 1) % pageImages.length);
  };

  const prevImage = () => {
    setIsLoading(true);
    setIsLandscape(false); // Reset landscape state when changing images
    setCurrentImageIndex((prev) => (prev - 1 + pageImages.length) % pageImages.length);
  };

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    // Check if image is landscape (wider than tall)
    setIsLandscape(img.naturalWidth > img.naturalHeight);
    setIsLoading(false);
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    setIsLandscape(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setIsLandscape(false);
  };

  // Reset landscape state when image index changes
  useEffect(() => {
    setIsLandscape(false);
  }, [currentImageIndex]);

  const handleDownloadAsPdf = async () => {
    if (pageImages.length === 0 || !document?.documentId || !document?.userId) {
      showToast(t('document_page_no_images_error'), 'error');
      return;
    }

    // Check if any image is a PDF (if so, don't convert)
    const hasPdf = pageImages.some(url => 
      url.toLowerCase().includes('.pdf') || 
      document?.fileName?.toLowerCase().endsWith('.pdf')
    );

    if (hasPdf) {
      showToast(t('document_page_already_pdf'), 'info');
      return;
    }

    setIsDownloadingPdf(true);
    try {
      showToast(t('document_page_creating_pdf'), 'info');
      
      // Call cloud function - it returns the PDF directly as a stream
      const response = await fetch('https://europe-west3-thescon.cloudfunctions.net/downloadInvoiceAsPdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: document.userId, // Use document.userId (the owner of the invoice)
          invoiceId: document.id, // Use document.id as invoiceId
          documentId: document.documentId, // Also send documentId as fallback
        })
      });

      // Check content type to determine if it's a PDF or JSON error
      const contentType = response.headers.get('content-type') || '';
      
      if (!response.ok) {
        // Try to parse error message from JSON response
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          // If not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Check if response is actually a PDF
      if (!contentType.includes('application/pdf')) {
        // Might be a JSON error even with 200 status
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Unexpected response format');
        } catch (jsonError) {
          if (jsonError instanceof Error && jsonError.message !== 'Unexpected response format') {
            throw jsonError;
          }
          throw new Error('Response is not a PDF');
        }
      }

      // The function returns the PDF directly as a blob
      const blob = await response.blob();
      
      // Verify it's actually a PDF by checking blob type
      if (!blob.type.includes('pdf') && blob.size === 0) {
        throw new Error('Received empty or invalid PDF');
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document?.fileName 
        ? document.fileName.replace(/\.[^/.]+$/, '') + '.pdf'
        : `invoice_${document.id || documentId || 'download'}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      
      // Clean up the object URL
      window.URL.revokeObjectURL(url);
      
      showToast(t('document_page_pdf_download_success'), 'success');
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error creating PDF:', error);
      }
      const errorMessage = error instanceof Error ? error.message : t('document_page_pdf_download_error');
      showToast(errorMessage, 'error');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleDownloadOriginal = async () => {
    const fileUrl = projectDocumentData?.fileUrl;
    const storagePath = projectDocumentData?.storagePath;
    const name = projectDocumentData?.filename || document?.fileName || 'document.docx';
    try {
      let url = fileUrl;
      if (!url && storagePath) {
        url = await getDownloadURL(ref(storage, storagePath));
      }
      if (!url) {
        showToast(t('document_page_pdf_download_error'), 'error');
        return;
      }
      const a = window.document.createElement('a');
      a.href = url;
      a.download = name;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      window.document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      showToast(t('document_page_pdf_download_error'), 'error');
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/');
      }
    });
    return () => unsub();
  }, [navigate]);

  // Handle viewport width changes - show Document tab only on mobile/tablet
  useEffect(() => {
    const handleResize = () => {
      setIsMobileOrTablet(window.innerWidth < 1024);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const showDocxNotice =
    !!(projectId || knowledgeId) &&
    !!projectDocumentData &&
    isDocxFileNameOrType(projectDocumentData.filename, projectDocumentData.contentType);

  const ingestV2 = projectDocumentData?.ingestV2;
  const derivedChunksCreated =
    typeof projectDocumentData?.chunksCreated === 'number'
      ? projectDocumentData.chunksCreated
      : typeof ingestV2?.chunkCount === 'number'
        ? ingestV2.chunkCount
        : 0;
  const derivedEmbeddingEnabled =
    typeof projectDocumentData?.embeddingEnabled === 'boolean'
      ? projectDocumentData.embeddingEnabled
      : Boolean(ingestV2);
  const derivedEmbeddingSucceeded =
    typeof projectDocumentData?.embeddingSucceeded === 'boolean'
      ? projectDocumentData.embeddingSucceeded
      : (projectDocumentData?.status === 'completed' && derivedChunksCreated > 0);
  const hasExtractionInfo =
    typeof projectDocumentData?.extractedCharCount === 'number' || Boolean(ingestV2);

  const deleteTitle = `Delete "${document?.title ?? t('document_page_this_document')}"?`;
  const deleteParagraph = t('document_page_delete_paragraph');
  const askThisDocument = () => {
    const selectedId = String(
      projectDocumentData?.documentId ||
      document?.documentId ||
      document?.id ||
      documentId ||
      ''
    );
    if (!selectedId) return;
    const path = buildThesconAiPath({
      projectId: projectId || undefined,
      knowledgeId: knowledgeId || undefined,
      projectDocumentIds: projectId ? [selectedId] : undefined,
      knowledgeDocumentIds: knowledgeId ? [selectedId] : undefined,
    });
    navigate(path);
  };

  return (
    <div className={styles.container}>

      {/* App bar */}
      <AppBar />

      {/* Content */}
      <ContentGrid
        rows='64px auto 1fr 80px'
        tabletRows='64px auto 1fr 80px'
        mobileRows='64px auto 1fr 80px'
      >

        {/* Toolbar left side */}
        <div className={styles.homeToolbarLeft}>

          {/* Back button */}
          <div className={styles.backButton}>
            <GlobalMicroButton
              title={t('document_page_navigate_back')}
              icon={<img src={isDarkMode ? "/assets/icons/chevron.left.svg" : "/assets/icons/chevron.left.black.svg"} alt="Left" width={8} height={8} />}
              onClick={() => knowledgeId ? navigate(`/knowledge/${knowledgeId}`) : projectId ? navigate(`/project/${projectId}`) : navigate(-1)}
            />
          </div>

          {/* Title */}
          <b>{t('document_page_document_details')}</b>
        </div>

        {/* Toolbar right side */}
        <div className={styles.homeToolbarRight}>
        </div>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerTitleRow}>
            <h1>{t('document_page_document_title') || 'Document'}</h1>
            {projectDocumentData?.version !== undefined && projectDocumentData?.version !== null && (
              <span className={styles.versionBadge}>v{projectDocumentData.version}</span>
            )}
            {(knowledgeId || projectId) && projectDocumentData && (() => {
              const badgeKind = getEmbeddingBadgeKind(
                derivedEmbeddingEnabled,
                derivedEmbeddingSucceeded,
                derivedChunksCreated,
                projectDocumentData.status
              );
              return (
                <span
                  className={`${styles.embeddingBadge} ${styles[`embeddingBadge_${badgeKind}`]}`}
                  title={
                    projectDocumentData.embeddingError
                      ? String(projectDocumentData.embeddingError)
                      : undefined
                  }
                >
                  {t(`document_page_embedding_badge_${badgeKind}`)}
                </span>
              );
            })()}
          </div>
          <p className={styles.headerSubtitle}>{projectDocumentData?.filename || document?.title || document?.fileName || t('document_page_unknown')}</p>
        </div>

        {/* Tabs */}
        <div className={styles.homeTabs}>
          <GlobalTabs
            queryParam="tab"
            defaultTabValue="summary"
            tabs={[
              {
                label: t('document_page_summary_tab'),
                queryValue: 'summary',
                content: (
                  <div className={styles.summary}>
                    {!document ? (
                      <div className={styles.loadingContent}>
                        <p>{t('document_page_loading_document')}</p>
                      </div>
                    ) : projectDocumentData ? (
                      <>
                        {/* Project Document Information */}
                        <span className={styles.sectionLabel}>{t('document_page_basic_information')}</span>

                        {/* File Link - moved up with icon */}
                        {projectDocumentData.fileUrl && (
                          <div className={styles.sectionRow}>
                            <img
                              src={isDarkMode ? '/assets/icons/link.svg' : '/assets/icons/link.black.svg'}
                              alt=""
                              width={16}
                              height={16}
                            />
                            <span className={styles.label}>{t('document_page_link') || 'Link'}:</span>
                            <a href={projectDocumentData.fileUrl} target="_blank" rel="noopener noreferrer" className={styles.link}>
                              {t('document_page_open_document') || 'Open document'}
                            </a>
                          </div>
                        )}

                        <div className={styles.sectionRow}>
                          <img
                            src={fileTypeGraphicPathOrGeneric(
                              projectDocumentData.filename || '',
                              projectDocumentData.contentType,
                              isDarkMode,
                            )}
                            alt=""
                            width={16}
                            height={16}
                          />
                          <span className={styles.label}>{t('document_page_filename')}:</span>
                          <p className={styles.sectionRowFilenameValue} title={projectDocumentData.filename || undefined}>
                            {projectDocumentData.filename || t('document_page_na')}
                          </p>
                        </div>

                        {projectDocumentData.version !== undefined && projectDocumentData.version !== null && (
                          <div className={styles.sectionRow}>
                            <img src={isDarkMode ? "/assets/icons/list.number.svg" : "/assets/icons/list.number.black.svg"} alt="Version" width={16} height={16} />
                            <span className={styles.label}>{t('add_document_version') || 'Version'}:</span>
                            <p>{projectDocumentData.version}</p>
                          </div>
                        )}

                        <div className={styles.sectionRow}>
                          <img src={isDarkMode ? "/assets/icons/clock.svg" : "/assets/icons/clock.black.svg"} alt="Created" width={16} height={16} />
                          <span className={styles.label}>{t('document_page_created')}:</span>
                          <p>{formatDate(
                            projectDocumentData.createdAt instanceof Timestamp 
                              ? projectDocumentData.createdAt.toDate() 
                              : projectDocumentData.createdAt 
                                ? new Date(projectDocumentData.createdAt) 
                                : undefined,
                            locale || 'en-US'
                          ) || t('document_page_na')}</p>
                        </div>

                        {(knowledgeId || projectId) && (
                          <div className={styles.knowledgeExtractionPanel}>
                            <GlobalCollapsibleButton title={t('document_page_extraction_section')} isOpen>
                              {hasExtractionInfo ? (
                                <div className={styles.extractionList}>
                                <div className={styles.extractionLine}>
                                  <span className={styles.extractionLabel}>{t('document_page_chars_extracted')}</span>
                                  <span className={styles.extractionValue}>
                                    {typeof projectDocumentData.extractedCharCount === 'number'
                                      ? new Intl.NumberFormat(locale || 'en-US').format(projectDocumentData.extractedCharCount)
                                      : t('document_page_na')}
                                  </span>
                                </div>
                                <div className={styles.extractionLine}>
                                  <span className={styles.extractionLabel}>{t('document_page_words_extracted')}</span>
                                  <span className={styles.extractionValue}>
                                    {typeof projectDocumentData.extractedWordCount === 'number'
                                      ? new Intl.NumberFormat(locale || 'en-US').format(projectDocumentData.extractedWordCount)
                                      : t('document_page_na')}
                                  </span>
                                </div>
                                <div className={styles.extractionLine}>
                                  <span className={styles.extractionLabel}>{t('document_page_tokens_estimated')}</span>
                                  <span className={styles.extractionValue}>
                                    {typeof projectDocumentData.estimatedTokenCount === 'number'
                                      ? new Intl.NumberFormat(locale || 'en-US').format(projectDocumentData.estimatedTokenCount)
                                      : t('document_page_na')}
                                  </span>
                                </div>
                                <div className={styles.extractionLine}>
                                  <span className={styles.extractionLabel}>{t('document_page_chunk_size')}</span>
                                  <span className={styles.extractionValue}>
                                    {projectDocumentData.chunkSize != null
                                      ? String(projectDocumentData.chunkSize)
                                      : t('document_page_na')}
                                  </span>
                                </div>
                                <div className={styles.extractionLine}>
                                  <span className={styles.extractionLabel}>{t('document_page_chunk_overlap')}</span>
                                  <span className={styles.extractionValue}>
                                    {projectDocumentData.chunkOverlap != null
                                      ? String(projectDocumentData.chunkOverlap)
                                      : t('document_page_na')}
                                  </span>
                                </div>
                                <div className={styles.extractionLine}>
                                  <span className={styles.extractionLabel}>{t('document_page_chunks_created')}</span>
                                  <span className={styles.extractionValue}>
                                    {new Intl.NumberFormat(locale || 'en-US').format(derivedChunksCreated)}
                                  </span>
                                </div>
                                <div className={styles.extractionLine}>
                                  <span className={styles.extractionLabel}>{t('document_page_embedding_status')}</span>
                                  <span className={styles.extractionValueSoft}>
                                    {(() => {
                                      const k = getEmbeddingBadgeKind(
                                        derivedEmbeddingEnabled,
                                        derivedEmbeddingSucceeded,
                                        derivedChunksCreated,
                                        projectDocumentData.status
                                      );
                                      const short = t(`document_page_embedding_badge_${k}`);
                                      const mark = k === 'ok' ? '✓' : k === 'ingesting' ? '☕' : '✗';
                                      return (
                                        <>
                                          {mark} {short}
                                          {projectDocumentData.embeddingError ? (
                                            <span className={styles.embeddingErrorNote}> — {String(projectDocumentData.embeddingError)}</span>
                                          ) : null}
                                        </>
                                      );
                                    })()}
                                  </span>
                                </div>
                                <div className={styles.extractionLine}>
                                  <span className={styles.extractionLabel}>{t('document_page_chunks_in_firestore')}</span>
                                  <span className={styles.extractionValueSoft}>
                                    {(() => {
                                      const stored = derivedChunksCreated;
                                      const fmt = (n: number) =>
                                        new Intl.NumberFormat(locale || 'en-US').format(n);
                                      if (verifiedChunkCount === undefined) {
                                        return '…';
                                      }
                                      if (verifiedChunkCount === null) {
                                        return (
                                          <>
                                            ✗ {t('document_page_extraction_chunk_unverified_short')}
                                          </>
                                        );
                                      }
                                      const live = verifiedChunkCount;
                                      if (stored > 0 && live === 0) {
                                        return (
                                          <>
                                            ✗ {t('document_page_extraction_chunk_none_found')}
                                          </>
                                        );
                                      }
                                      if (live === stored) {
                                        return (
                                          <>
                                            ✓ {fmt(live)}
                                          </>
                                        );
                                      }
                                      return (
                                        <>
                                          ✗ {fmt(live)}{' '}
                                          <span className={styles.chunkMismatch}>
                                            ({t('document_page_chunk_count_mismatch')})
                                          </span>
                                        </>
                                      );
                                    })()}
                                  </span>
                                </div>
                                </div>
                              ) : (
                                <p className={styles.extractionEmpty}>{t('document_page_no_extraction_stats')}</p>
                              )}
                            </GlobalCollapsibleButton>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Invoice Document Information */}
                        <span className={styles.sectionLabel}>{t('document_page_basic_information')}</span>

                        <div className={styles.sectionRow}>
                          <img src={isDarkMode ? "/assets/icons/document.on.document.svg" : "/assets/icons/document.on.document.black.svg"} alt="Title" width={16} height={16} />
                          <span className={styles.label}>{t('document_page_title')}:</span>
                          <p>{document.title || t('document_page_na')}</p>
                        </div>

                        <div className={styles.sectionRow}>
                          {document.senderWebsiteUrl ? (
                            <Favicon 
                              websiteUrl={document.senderWebsiteUrl} 
                              size={16} 
                              alt="Sender favicon"
                            />
                          ) : (
                            <img src={isDarkMode ? "/assets/icons/building.2.svg" : "/assets/icons/building.2.black.svg"} alt="Sender" width={16} height={16} />
                          )}
                          <span className={styles.label}>{t('document_page_sender')}:</span>
                          <p>{document.sender || t('document_page_na')}</p>
                        </div>

                        {document.receiver && (
                          <div className={styles.sectionRow}>
                            <img src={isDarkMode ? "/assets/icons/arrowshape.right.fill.svg" : "/assets/icons/arrowshape.right.fill.black.svg"} alt="Receiver" width={16} height={16} />
                            <span className={styles.label}>{t('document_page_receiver')}:</span>
                            <p>{document.receiver}</p>
                          </div>
                        )}

                        {/* Invoice Details */}
                        {(document.invoiceNumber || document.invoiceDate || document.dueDate) && (
                          <>
                            <span className={styles.sectionLabel}>{t('document_page_invoice_details')}</span>

                            {document.invoiceNumber && (
                              <div className={styles.sectionRow}>
                                <img src={isDarkMode ? "/assets/icons/list.number.svg" : "/assets/icons/list.number.black.svg"} alt="Invoice Number" width={16} height={16} />
                                <span className={styles.label}>{t('document_page_invoice_number')}:</span>
                                <p>{document.invoiceNumber}</p>
                              </div>
                            )}

                            {document.invoiceDate && (
                              <div className={styles.sectionRow}>
                                <img src={isDarkMode ? "/assets/icons/clock.svg" : "/assets/icons/clock.black.svg"} alt="Invoice Date" width={16} height={16} />
                                <span className={styles.label}>{t('document_page_invoice_date')}:</span>
                                <p>{document.invoiceDate}</p>
                              </div>
                            )}

                            {document.dueDate && (
                              <div className={styles.sectionRow}>
                                <img src={isDarkMode ? "/assets/icons/timer.circle.svg" : "/assets/icons/timer.circle.black.svg"} alt="Due Date" width={16} height={16} />
                                <span className={styles.label}>{t('document_page_due_date')}:</span>
                                <p>{document.dueDate}</p>
                              </div>
                            )}

                            {document.paymentTerms && (
                              <div className={styles.sectionRow}>
                                <img src={isDarkMode ? "/assets/icons/creditcard.svg" : "/assets/icons/creditcard.black.svg"} alt="Payment Terms" width={16} height={16} />
                                <span className={styles.label}>{t('document_page_payment_terms')}:</span>
                                <p>{document.paymentTerms}</p>
                              </div>
                            )}
                          </>
                        )}

                        {/* Financial Information */}
                        {(document.subtotal || document.taxAmount || document.totalAmount || document.currency) && (
                          <>
                            <span className={styles.sectionLabel}>{t('document_page_financial_information')}</span>

                            {document.subtotal && (
                              <div className={styles.sectionRow}>
                                <img src={isDarkMode ? "/assets/icons/creditcard.svg" : "/assets/icons/creditcard.black.svg"} alt="Subtotal" width={16} height={16} />
                                <span className={styles.label}>{t('document_page_subtotal')}:</span>
                                <p>{document.currency} {document.subtotal}</p>
                              </div>
                            )}

                            {document.taxAmount && (
                              <div className={styles.sectionRow}>
                                <img src={isDarkMode ? "/assets/icons/creditcard.svg" : "/assets/icons/creditcard.black.svg"} alt="Tax Amount" width={16} height={16} />
                                <span className={styles.label}>{t('document_page_tax_amount')}:</span>
                                <p>{document.currency} {document.taxAmount}</p>
                              </div>
                            )}

                            {document.totalAmount && (
                              <div className={styles.sectionRow}>
                                <img src={isDarkMode ? "/assets/icons/creditcard.svg" : "/assets/icons/creditcard.black.svg"} alt="Total Amount" width={16} height={16} />
                                <span className={styles.label}>{t('document_page_total_amount')}:</span>
                                <p>{document.currency} {document.totalAmount}</p>
                              </div>
                            )}
                          </>
                        )}

                        {/* Document Information */}
                        <span className={styles.sectionLabel}>{t('document_page_document_information')}</span>

                        <div className={styles.sectionRow}>
                          <img src={isDarkMode ? "/assets/icons/clock.svg" : "/assets/icons/clock.black.svg"} alt="Created" width={16} height={16} />
                          <span className={styles.label}>{t('document_page_created')}:</span>
                          <p>{formatDate(document?.createdAt ? new Date(document.createdAt) : undefined, locale || 'en-US') || t('document_page_na')}</p>
                        </div>

                        <div className={styles.sectionRow}>
                          <img src={isDarkMode ? "/assets/icons/list.triangle.svg" : "/assets/icons/list.triangle.black.svg"} alt="Category" width={16} height={16} />
                          <span className={styles.label}>{t('document_page_category')}:</span>
                          <p>{document?.detectedDocumentCategory || t('document_page_na')}</p>
                        </div>

                        <div className={styles.sectionRow}>
                          <img src={isDarkMode ? "/assets/icons/checkmark.circle.svg" : "/assets/icons/checkmark.circle.black.svg"} alt="Status" width={16} height={16} />
                          <span className={styles.label}>{t('document_page_status')}:</span>
                          <p>{document?.status || t('document_page_na')}</p>
                        </div>

                        {document.senderWebsiteUrl && (
                          <div className={styles.sectionRow}>
                            <img src={isDarkMode ? "/assets/icons/icloud.and.arrow.up.svg" : "/assets/icons/icloud.and.arrow.up.black.svg"} alt="Website" width={16} height={16} />
                            <span className={styles.label}>Website:</span>
                            <a href={document.senderWebsiteUrl} target="_blank" rel="noopener noreferrer" className={styles.link}>
                              {document.senderWebsiteUrl}
                            </a>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ),
              },
              // Document tab - only show on mobile and tablet, not on desktop
              ...(isMobileOrTablet ? [{
                label: t('document_page_document_tab'),
                queryValue: 'document',
                content: (
                  <div className={styles.documentTab}>
                    {isLoading ? (
                      <div className={styles.previewContainer}>
                        <div className={styles.imageContainer}>
                          <div className={styles.loadingState}>
                            <div className={styles.spinner}></div>
                            <p>{t('document_page_loading_images')}</p>
                          </div>
                        </div>
                      </div>
                    ) : showDocxNotice ? (
                      <div className={styles.previewContainer}>
                        <div className={styles.docxPreviewNotice}>
                          <div className={styles.docxPreviewNoticeRow}>
                            <img
                              className={styles.docxPreviewLargeIcon}
                              src="/assets/graphics/docx_icon.svg"
                              alt=""
                            />
                            <div className={styles.docxPreviewNoticeText}>
                              <h3 className={styles.docxPreviewTitle}>
                                {t('document_page_docx_no_preview_title')}
                              </h3>
                              <p>{t('document_page_docx_no_preview')}</p>
                            </div>
                          </div>
                          <GlobalMicroButton
                            title={t('document_page_download_original')}
                            icon={<img src="/assets/icons/arrow.down.circle.black.svg" alt="" width={16} height={16} />}
                            onClick={() => { void handleDownloadOriginal(); }}
                            variant="secondary"
                          >
                            {t('document_page_download_original')}
                          </GlobalMicroButton>
                        </div>
                      </div>
                    ) : pageImages.length === 0 ? (
                      <div className={styles.previewContainer}>
                        <div className={styles.emptyState}>
                          <p>{t('document_page_no_images') || 'No document preview available'}</p>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.previewContainer}>
                        <div className={styles.imageContainer}>
                          {(() => {
                            const currentUrl = pageImages[currentImageIndex];
                            const isPdf = currentUrl.toLowerCase().includes('.pdf') || 
                                         document?.fileName?.toLowerCase().endsWith('.pdf');
                            
                            if (isPdf) {
                              return (
                                <div className={styles.pdfViewerWrapper}>
                                  <iframe
                                    src={currentUrl}
                                    className={styles.pdfViewer}
                                    title={`Document ${currentImageIndex + 1}`}
                                    onLoad={handleIframeLoad}
                                    onError={handleImageError}
                                    scrolling="no"
                                  />
                                </div>
                              );
                            } else {
                              return (
                                <>
                                  <div className={`${styles.imageWrapper} ${isLandscape ? styles.rotated : ''}`}>
                                    <img
                                      src={currentUrl}
                                      alt={`Page ${currentImageIndex + 1}`}
                                      className={`${styles.previewImage} ${isLandscape ? styles.rotated : ''}`}
                                      onLoad={handleImageLoad}
                                      onError={handleImageError}
                                    />
                                  </div>
                                  <div className={styles.downloadButtonWrapper}>
                                    <GlobalMicroButton
                                      title={t('document_page_download_pdf')}
                                      icon={<img src="/assets/icons/arrow.down.circle.black.svg" alt={t('document_page_download')} width={16} height={16} />}
                                      onClick={handleDownloadAsPdf}
                                      disabled={isDownloadingPdf}
                                      isLoading={isDownloadingPdf}
                                      variant="secondary"
                                    >
                                      {isDownloadingPdf ? t('document_page_creating_pdf') : t('document_page_download_pdf')}
                                    </GlobalMicroButton>
                                  </div>
                                </>
                              );
                            }
                          })()}
                        </div>

                        {pageImages.length > 1 && !isLoading && (
                          <div className={styles.navigationControls}>
                            <button
                              onClick={prevImage}
                              className={styles.navButton}
                              disabled={pageImages.length <= 1}
                            >
                              <img src={isDarkMode ? "/assets/icons/chevron.left.svg" : "/assets/icons/chevron.left.black.svg"} alt={t('document_page_previous')} width={16} height={16} />
                            </button>

                            <span className={styles.pageIndicator}>
                              {currentImageIndex + 1} / {pageImages.length}
                            </span>

                            <button
                              onClick={nextImage}
                              className={styles.navButton}
                              disabled={pageImages.length <= 1}
                            >
                              <img src={isDarkMode ? "/assets/icons/chevron.right.svg" : "/assets/icons/chevron.right.black.svg"} alt={t('document_page_next')} width={16} height={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ),
              }] : []),
              {
                label: t('document_page_metadata_tab') || 'Metadata',
                queryValue: 'edit',
                content: (
                  <div className={styles.metadataTab}>
                    {!document ? (
                      <div className={styles.loadingContent}>
                        <p>{t('document_page_loading_document')}</p>
                      </div>
                    ) : projectDocumentData ? (
                      <div className={styles.metadataForm}>
                        <div className={styles.formSection}>
                          <span className={styles.sectionLabel}>{t('document_page_basic_information')}</span>
                          
                          <GlobalInput
                            label={t('document_page_filename') || 'Filename'}
                            value={editedMetadata.filename || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, filename: e.target.value })}
                          />
                          
                          <GlobalInput
                            label={t('add_document_version') || 'Version Number (optional)'}
                            type="number"
                            value={editedMetadata.version || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, version: e.target.value })}
                            placeholder={t('add_document_version_placeholder') || 'e.g., 1.0'}
                          />
                        </div>

                        <div className={styles.formActions}>
                          <GlobalButton
                            variant="primary"
                            onClick={handleSaveMetadata}
                            disabled={isSavingMetadata}
                            isLoading={isSavingMetadata}
                          >
                            {isSavingMetadata ? (t('document_page_saving') || 'Saving...') : (t('document_page_save_metadata') || 'Save Metadata')}
                          </GlobalButton>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.metadataForm}>
                        <div className={styles.formSection}>
                          <span className={styles.sectionLabel}>{t('document_page_basic_information')}</span>
                          
                          <GlobalInput
                            label={t('document_page_title')}
                            value={editedMetadata.title || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, title: e.target.value })}
                          />
                          
                          <GlobalInput
                            label={t('document_page_sender')}
                            value={editedMetadata.sender || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, sender: e.target.value })}
                          />
                          
                          <GlobalInput
                            label={t('document_page_receiver')}
                            value={editedMetadata.receiver || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, receiver: e.target.value })}
                          />
                          
                          <GlobalInput
                            label={t('document_page_category')}
                            value={editedMetadata.detectedDocumentCategory || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, detectedDocumentCategory: e.target.value })}
                          />
                          
                          <GlobalInput
                            label="Website"
                            value={editedMetadata.senderWebsiteUrl || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, senderWebsiteUrl: e.target.value })}
                          />
                          
                          <GlobalTextarea
                            label={t('document_page_summary') || 'Summary'}
                            value={editedMetadata.summary || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, summary: e.target.value })}
                            rows={3}
                          />
                        </div>

                        <div className={styles.formSection}>
                          <span className={styles.sectionLabel}>{t('document_page_invoice_details')}</span>
                          
                          <GlobalInput
                            label={t('document_page_invoice_number')}
                            value={editedMetadata.invoiceNumber || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, invoiceNumber: e.target.value })}
                          />
                          
                          <GlobalInput
                            label={t('document_page_invoice_date')}
                            value={editedMetadata.invoiceDate || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, invoiceDate: e.target.value })}
                          />
                          
                          <GlobalInput
                            label={t('document_page_due_date')}
                            value={editedMetadata.dueDate || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, dueDate: e.target.value })}
                          />
                          
                          <GlobalInput
                            label={t('document_page_payment_terms')}
                            value={editedMetadata.paymentTerms || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, paymentTerms: e.target.value })}
                          />
                        </div>

                        <div className={styles.formSection}>
                          <span className={styles.sectionLabel}>{t('document_page_financial_information')}</span>
                          
                          <GlobalInput
                            label={t('document_page_currency')}
                            value={editedMetadata.currency || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, currency: e.target.value })}
                          />
                          
                          <GlobalInput
                            label={t('document_page_subtotal')}
                            value={editedMetadata.subtotal || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, subtotal: e.target.value })}
                          />
                          
                          <GlobalInput
                            label={t('document_page_tax_amount')}
                            value={editedMetadata.taxAmount || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, taxAmount: e.target.value })}
                          />
                          
                          <GlobalInput
                            label={t('document_page_total_amount')}
                            value={editedMetadata.totalAmount || ''}
                            onChange={(e) => setEditedMetadata({ ...editedMetadata, totalAmount: e.target.value })}
                          />
                        </div>

                        <div className={styles.formActions}>
                          <GlobalButton
                            variant="primary"
                            onClick={handleSaveMetadata}
                            disabled={isSavingMetadata}
                            isLoading={isSavingMetadata}
                          >
                            {isSavingMetadata ? (t('document_page_saving') || 'Saving...') : (t('document_page_save_metadata') || 'Save Metadata')}
                          </GlobalButton>
                        </div>
                      </div>
                    )}
                  </div>
                ),
              },
              {
                label: t('document_page_options_tab') || 'Options',
                queryValue: 'options',
                content: (
                  <div className={styles.optionsTab}>
                    <div className={styles.optionsGrid}>

                      {/* Delete Document Card */}
                      <SmallServiceCard
                        id="delete-document-card"
                        title={t('document_page_delete_document') || 'Delete Document'}
                        description={t('document_page_delete_description') || 'Permanently delete this document and all its embeddings. This action cannot be undone.'}
                        fileTypeIcon={isDarkMode ? "/assets/icons/trash.red.svg" : "/assets/icons/trash.red.svg"}
                        serviceIcon=""
                        buttonIcon={isDarkMode ? "/assets/icons/trash.svg" : "/assets/icons/trash.black.svg"}
                        buttonText={t('document_page_delete_button') || 'Delete'}
                        buttonOnClick={() => setShowModal(true)}
                        brandColor="var(--wild-watermelon)"
                      />
                      
                      {/* Ask Document Card */}
                      <SmallServiceCard
                        id="ask-document-card"
                        title={t('document_page_ask_document') || 'Ask Document'}
                        description={t('document_page_ask_description') || 'Communicate with this document. Ask questions and get answers based on the document\'s content and embeddings.'}
                        fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
                        serviceIcon=""
                        buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" : "/assets/icons/arrow.right.circle.black.svg"}
                        buttonText={t('document_page_ask_button') || 'Ask'}
                        buttonOnClick={askThisDocument}
                      />
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>

        {/* Separator */}
        <div className={styles.gridSeparatorWrapper}>
          <div className={styles.gridSeparator} />
        </div>

        {/* Document Preview */}
        <div className={styles.documentPreview}>
          {isLoading ? (
            <div className={styles.previewContainer}>
              <div className={styles.loadingWrapper}>
                <div className={styles.loadingState}>
                  <div className={styles.spinner}></div>
                  <p>{t('document_page_loading_images')}</p>
                </div>
              </div>
            </div>
          ) : showDocxNotice ? (
            <div className={styles.previewContainer}>
              <div className={styles.docxPreviewNotice}>
                <div className={styles.docxPreviewNoticeRow}>
                  <img
                    className={styles.docxPreviewLargeIcon}
                    src="/assets/graphics/docx_icon.svg"
                    alt=""
                  />
                  <div className={styles.docxPreviewNoticeText}>
                    <h3 className={styles.docxPreviewTitle}>
                      {t('document_page_docx_no_preview_title')}
                    </h3>
                    <p>{t('document_page_docx_no_preview')}</p>
                  </div>
                </div>
                <GlobalMicroButton
                  title={t('document_page_download_original')}
                  icon={<img src="/assets/icons/arrow.down.circle.black.svg" alt="" width={16} height={16} />}
                  onClick={() => { void handleDownloadOriginal(); }}
                  variant="secondary"
                >
                  {t('document_page_download_original')}
                </GlobalMicroButton>
              </div>
            </div>
          ) : pageImages.length === 0 ? (
            <div className={styles.previewContainer}>
              <div className={styles.emptyState}>
                <p>{t('document_page_no_images') || 'No document images available'}</p>
              </div>
            </div>
          ) : (
            <div className={styles.previewContainer}>
              <div className={styles.imageContainer}>
                {(() => {
                  const currentUrl = pageImages[currentImageIndex];
                  const isPdf = currentUrl.toLowerCase().includes('.pdf') || 
                               document?.fileName?.toLowerCase().endsWith('.pdf');
                  
                  if (isPdf) {
                    return (
                      <div className={styles.pdfViewerWrapper}>
                        <iframe
                          src={currentUrl}
                          className={styles.pdfViewer}
                          title={`Page ${currentImageIndex + 1}`}
                          onLoad={handleIframeLoad}
                          onError={handleImageError}
                          scrolling="no"
                        />
                      </div>
                    );
                  } else {
                    return (
                      <>
                        <div className={`${styles.imageWrapper} ${isLandscape ? styles.rotated : ''}`}>
                          <img
                            src={currentUrl}
                            alt={`Page ${currentImageIndex + 1}`}
                            className={`${styles.previewImage} ${isLandscape ? styles.rotated : ''}`}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                          />
                        </div>
                        <div className={styles.downloadButtonWrapper}>
                          <GlobalMicroButton
                            title={t('document_page_download_pdf')}
                            icon={<img src="/assets/icons/arrow.down.circle.black.svg" alt={t('document_page_download')} width={16} height={16} />}
                            onClick={handleDownloadAsPdf}
                            disabled={isDownloadingPdf}
                            isLoading={isDownloadingPdf}
                            variant="secondary"
                          >
                            {isDownloadingPdf ? t('document_page_creating_pdf') : t('document_page_download_pdf')}
                          </GlobalMicroButton>
                        </div>
                      </>
                    );
                  }
                })()}
              </div>

              {pageImages.length > 1 && !isLoading && (
                <div className={styles.navigationControls}>
                  <button
                    onClick={prevImage}
                    className={styles.navButton}
                    disabled={pageImages.length <= 1}
                  >
                    <img src={isDarkMode ? "/assets/icons/chevron.left.svg" : "/assets/icons/chevron.left.black.svg"} alt="Previous" width={16} height={16} />
                  </button>

                  <span className={styles.pageIndicator}>
                    {currentImageIndex + 1} / {pageImages.length}
                  </span>

                  <button
                    onClick={nextImage}
                    className={styles.navButton}
                    disabled={pageImages.length <= 1}
                  >
                    <img src={isDarkMode ? "/assets/icons/chevron.right.svg" : "/assets/icons/chevron.right.black.svg"} alt="Next" width={16} height={16} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <AppFooter inline />

      </ContentGrid>

      {/* Toast */}
      <Toast
        message={message || ''}
        type={type}
        isVisible={isVisible}
        onClose={hideToast}
      />

      {/* Deletion modal */}
      <DoubleConfirmationModal
        isOpen={showModal}
        title={deleteTitle}
        paragraph={deleteParagraph}
        primaryLabel={t('document_page_confirm_deletion')}
        primaryIcon="/assets/icons/trash.red.svg"
        secondaryLabel={t('document_page_cancel')}
        secondaryIcon="/assets/icons/xmark.svg"
        onPrimary={handleDelete}
        onSecondary={handleCancel}
        primaryDestructive
        loading={isDeleting}
      />

      {/* Ask Document Sidebar */}
      <GlobalSidebar
        isOpen={showAskDocumentSidebar}
        onClose={() => setShowAskDocumentSidebar(false)}
      >
        <div>
          {/* Header */}
          <div className={styles.aiExpertSidebarHeader}>
            <img src={isDarkMode ? "/assets/identity/logo-dark.svg" : "/assets/identity/logo.svg"} alt="Thescon" width={16} height={16} />
            <span>|</span>
            <h2>{t('document_page_ask_document') || 'Ask Document'}</h2>
          </div>

          {/* Content */}
          <GlobalGptAccessPanel
            hideBorderTop
            projectId={projectId || undefined}
            knowledgeId={knowledgeId || undefined}
          />
        </div>
      </GlobalSidebar>

    </div>
  );
}
